// One call per step: the endpoint takes a single `targetStopId`, so a split selection needs several.
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { DatedRouteSlot } from 'types/api/route/routeSlotRequests'
import type { RouteId, RouteSlotId, ServiceDate } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface MoveRouteSlotStepArgs {
  readonly routeId: RouteId
  readonly serviceDate: ServiceDate
  readonly slots: ReadonlyArray<DatedRouteSlot>
  /** Insertion anchor — the endpoint inserts AFTER it; null lands at the top. */
  readonly targetRouteSlotId: RouteSlotId | null
  readonly updateMasterSequence?: boolean
  readonly runIndex?: number
}

export type MoveRouteSlotStepValue = { readonly stopCount: number }

export type MoveRouteSlotStepResult = StepResult<'MOVE_ROUTE_SLOT', MoveRouteSlotStepValue>

export const makeMoveRouteSlotStep = (
  args: MoveRouteSlotStepArgs,
  deps: Deps
): PipelineStep<'MOVE_ROUTE_SLOT', MoveRouteSlotStepResult> => {
  const stepId = `move-route-slot:${args.routeId}:${args.serviceDate}:${args.runIndex ?? 0}`
  return {
    id: stepId,
    phase: 'MOVE_ROUTE_SLOT',
    label: 'Failed to move stops',
    applyOrder: 100 + (args.runIndex ?? 0),
    run: async () => {
      if (!deps.moveRouteSlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'moveRouteSlot dep not provided — the v3-slot strategy requires it' })
      }
      if (args.slots.length === 0) {
        return ok({ phase: 'MOVE_ROUTE_SLOT', stepId, value: { stopCount: 0 } })
      }

      try {
        const response = await deps
          .moveRouteSlot({
            routeId: args.routeId,
            data: {
              targetStopId: args.targetRouteSlotId,
              serviceDate: args.serviceDate,
              stops: [...args.slots],
              updateMasterSequence: args.updateMasterSequence ?? false,
            },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({ phase: 'MOVE_ROUTE_SLOT', stepId, value: { stopCount: response?.stopCount ?? 0 } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'MOVE_ROUTE_SLOT', status, message })
      }
    },
  }
}
